"use client";

import { Button } from "@/components/ui/button";
import { StepCard } from "./StepCard";
import type { StepStatus, AccountBalances } from "./types";

interface Step5Props {
  status: StepStatus;
  isConnected: boolean;
  activeAccountId: string;
  balances: AccountBalances;
  onDeposit: () => void;
  onGoBack?: () => void;
}

export function Step5DepositStrategy({
  status,
  isConnected,
  activeAccountId,
  balances,
  onDeposit,
  onGoBack,
}: Step5Props) {
  const isProcessing = status === "in-progress";
  const usdcAvailable = balances.usdc;
  const xlmAvailable = balances.xlm;

  return (
    <StepCard
      stepNumber={5}
      title="Deposit into LP"
      description="Provide liquidity across two pools to earn yield"
      status={status}
      onGoBack={onGoBack}
      completedSummary={
        <span>Deposited USDC + XLM across Aquarius and Soroswap</span>
      }
    >
      <div className="space-y-4">
        <div className="text-center py-2">
          <div className="text-2xl font-bold font-mono">{usdcAvailable} USDC + {xlmAvailable} XLM</div>
          <div className="text-sm text-muted-foreground">Available balance, 50/50 split across Aquarius + Soroswap</div>
        </div>

        {/* Split visualization */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-muted rounded-md p-3 text-center">
            <div className="text-xs text-muted-foreground mb-1">Aquarius Pool</div>
            <div className="font-mono font-semibold text-sm">USDC + XLM</div>
            <div className="text-xs text-muted-foreground">Reward-bearing LP</div>
          </div>
          <div className="bg-muted rounded-md p-3 text-center">
            <div className="text-xs text-muted-foreground mb-1">Soroswap Pool</div>
            <div className="font-mono font-semibold text-sm">USDC + XLM</div>
            <div className="text-xs text-muted-foreground">Fee-earning LP</div>
          </div>
        </div>

        <div className="text-xs text-muted-foreground bg-muted rounded-md p-3">
          Deposits all available USDC and XLM (minus 10 XLM reserve for fees). Exact amounts split equally between pools.
        </div>

        <Button
          onClick={onDeposit}
          disabled={!activeAccountId || !isConnected || isProcessing}
          className="w-full"
        >
          {isProcessing ? "Depositing..." : "Deposit into LP Pools"}
        </Button>
      </div>
    </StepCard>
  );
}
